import type { LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";

export interface SideTab<T extends string> {
  value: T;
  label: string;
  /** Optional count, drawn as a chip after the label. Leave it out for an empty
   *  set, exactly as in `PillTabs`: a column of "0" chips reads as a stack of
   *  counters that failed, and the panel's own empty card says it in words. */
  count?: number;
  icon?: LucideIcon;
  /** A second line under the label, shown only in the wide column. Optional:
   *  a strip of one-word tabs is clearer without a sentence under each. */
  desc?: string;
}

/**
 * The same tab set as `PillTabs`, stood on its end: a menu down the left of a
 * long record, pinned while the panel beside it scrolls.
 *
 * It is the Settings side menu's control (`.crm-snav`) driven by state rather
 * than by the router, because the pages that need it switch a panel inside one
 * component and have no route per tab. The look is deliberately identical - an
 * operator should not have to learn that this column of entries works like the
 * other column of entries.
 *
 * WHY IT IS STICKY WITHOUT A WRAPPER. This element is meant to BE the grid item,
 * so its containing block is the grid area, which is as tall as the panel next
 * to it - that is the travel the pin needs. Put it inside another div in the
 * grid cell and `items-start` shrinks that div to the menu's own height, the pin
 * travels one screenful and then scrolls away.
 *
 * Below `lg` it turns back into a horizontal strip that scrolls sideways, with
 * no scrollbar drawn: a 214px column costs a phone the whole panel.
 */
export function SideTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: readonly SideTab<T>[];
  value: T;
  onChange: (v: T) => void;
  /** The menu's accessible name - the record's own word for these parts. */
  label: string;
  className?: string;
}) {
  return (
    <nav
      aria-label={label}
      role="tablist"
      aria-orientation="vertical"
      className={cn(
        "flex gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:sticky lg:top-[var(--crm-nav-top)] lg:flex-col lg:overflow-visible [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const on = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={on}
            className={cn("crm-snav shrink-0 lg:w-full", on && "is-on")}
            onClick={() => onChange(tab.value)}
          >
            {Icon ? <Icon className="mt-[3px] size-4 shrink-0" /> : null}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              {/* One line in the horizontal strip, free to wrap in the column -
                  a two-word label is not worth widening every entry for. */}
              <span className="whitespace-nowrap text-[12.5px] font-[650] lg:whitespace-normal">{tab.label}</span>
              {tab.desc ? (
                <span className="hidden text-[11px] font-[550] leading-snug opacity-70 lg:block">{tab.desc}</span>
              ) : null}
            </span>
            {typeof tab.count === "number" ? <span className="crm-tab-n ml-auto self-start">{tab.count}</span> : null}
          </button>
        );
      })}
    </nav>
  );
}
